import { TWidget } from 'widgets/Widget';
import { scrollWindowTo } from 'widgets/toolbox/scroll';
import {
    appendParamsToUrl,
    getUrlParams,
    get
} from 'widgets/toolbox/util';

import { TShippingFormInstance } from 'widgets/checkout/ShippingForm';
import { TBillingFormInstance } from 'widgets/checkout/BillingForm';
import { TRefElementInstance } from 'widgets/toolbox/RefElement';
import { TOrderModel } from 'types/models/order';

export type TPaymentMethod = {
    name: string;
    id: string;
};

/**
 * @param Widget mixin to handle focus
 * @returns Checkout Manager widget
 */

function CheckoutMgrClassCreator(Widget: TWidget) {
    /**
     * @category widgets
     * @subcategory checkout
     * @class CheckoutMgr
     * @augments Widget
     * @property {string} shippingFormID - Shipping form ID
     * @property {string} billingFormID - Billing form ID
     * @property {string} stepsList - List of checkout steps
     * @property {object} data-basket - Basket data object
     * @property {object} data-customer - Customer data object
     * @property {string} data-url-checkout - Checkout begin URL
     * @property {string} data-url-place-order - Place order URL
     * @property {string} data-shipping-skipped - Is the shipping step skipped
     * @classdesc CheckoutMgr component on checkout page. Widget has next functionality:
     * 1. Manage checkout steps activation/deactivation/submission
     * 2. Update summary block
     * 3. Handles popstate event
     *
     * Widget has next relationship:
     * * From initialization method renders shipping methods list by calling {@link ShippingForm#updateState}
     * * Activates shipping/billing step by calling {@link ShippingForm#activate}/{@link BillingForm#activate}
     * * Submits shipping/billing form by calling {@link ShippingForm#submit}/{@link BillingForm#submit}
     * * Checks privacy checkbox status before summary submission by calling {@link InputCheckbox#validate}
     * * Sets billing errors by calling {@link BillingForm#setError}
     * * Listens {@link ShippingForm#event:updateshippingform} to update summary
     * * Listens {@link ShippingForm#submit}/{@link BillingForm#submit} to update summary and go to the next step
     * * Listens `dom#click` on the next step button to go to the next step
     * * Listens `dom#click` on the edit step button to edit one of the previous step
     *
     * @example <caption>Checkout manager</caption>
     * <div
     *    id="checkout-main"
     *    class="l-checkout"
     *    data-url-place-order="${URLUtils.url('CheckoutServices-PlaceOrder')}"
     *    data-url-checkout="${URLUtils.url('Checkout-Begin')}"
     *    data-widget="checkoutMgr"
     *    data-basket="${JSON.stringify(pdict.order)}"
     *    data-customer="${JSON.stringify(pdict.customer)}"
     *  >
     *    ...
     *    <isinclude template="checkout/shipping/shipping" />
     *    <isinclude template="checkout/billing/billing" />
     *    <isinclude template="checkout/summary/summary" />
     *    ...
     *    <isinclude template="m/checkout/orderTotalSummary" />
     *    ...
     *    <isinclude template="m/checkout/orderProductSummary" />
     *    ...
     * </div>
     */
    class CheckoutMgr extends Widget {
        currentStep = '';

        prevStep = '';

        isStepActivatedFromHistory = false;

        order?: TOrderModel;

        customer?: Record<string, unknown> & {
            profile: {
                firstName: string;
            };
        };

        get currentFormId() {
            return this.currentStep === 'shipping' ? this.prefs().shippingFormID : this.prefs().billingFormID;
        }

        prefs() {
            return {
                shippingFormID: 'dwfrm_shipping',
                billingFormID: 'dwfrm_billing',
                stepsList: 'shipping,billing,summary',
                processingRequest: false,
                basket: {
                    steps: {},
                    orderEmail: null,
                    shipping: []
                },
                customer: {
                    profile: {
                        firstName: ''
                    }
                },
                urlCheckout: '',
                urlPlaceOrder: '',
                urlCart: '',
                simpleShipping: false,
                skipSummaryStep: false,
                displayCustomerMessage: false,
                isGuestCheckoutFlowRestricted: false,
                ...super.prefs()
            };
        }

        /**
         * @description Handle activate step logic
         */
        activateStep() {
        }

        /**
         * @description Handle deactivate step logic
         * @param stepName Step name that will be deactivate
         * @param isStepFinished - Flag to detect that step already finished
         */
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        deactivateStep(stepName: string, isStepFinished: boolean) {
        }

        /**
         * @description Initialize widget logic
         * * render shipping methods list
         * * activate current step
         * * init events
         * * init properties
         * @listens "checkout.paymentmethod.updated"

         */
        init() {
            super.init();

            this.postInit();
            this.initEvents();
            this.eventBus().on('checkout.paymentmethod.updated', 'onPaymentUpdate');
            this.eventBus().on('customer.login', 'onCustomerLogin');
            this.eventBus().on('customer.registered', 'onCustomerLogin');
            this.eventBus().on('coupon.updated', 'onCouponUpdated');
        }

        /**
         * @description Executes on coupon code adding/deleting
         * @emits "checkout.order.updated"
         * @param order Order model
         */
        onCouponUpdated(order: TOrderModel) {
            this.order = order;
            this.eventBus().emit('order.updated', this.order);
        }

        /**
         * @description Executes on payment method update
         * @emits "checkout.paymentmethod.changed"
         * @param paymentMethod Payment method object
         */
        onPaymentUpdate(paymentMethod: TPaymentMethod) {
            this.eventBus().emit('checkout.paymentmethod.changed', paymentMethod.name, this.order);
        }

        /**
         * @description Post-Initialize widget logic
         * * render shipping methods list
         * * activate current step
         */
        postInit() {
            const basket = this.prefs().basket;

            this.order = basket;
            this.customer = this.prefs().customer;

            this.currentStep = this.getInitialStep();

            this.getById(
                this.prefs().shippingFormID,
                (shippingFormComponent: TShippingFormInstance) => shippingFormComponent.callIfExists('updateState', basket)
            );

            this.activateStepByName(this.currentStep, true);
            this.callIfExists('setFocusOnFirstInput', this.currentStep);
        }

        /**
         * @description Get initialization checkout step
         * @returns Returns shipping/billing/summary step name
         */
        getInitialStep(): 'shipping' | 'billing' | 'summary' {
            const urlParams = <{ step: string; stage: string }> getUrlParams();

            const isShippingCompleted = this.order?.steps?.shipping?.iscompleted;
            const isBillingCompleted = this.order?.steps?.billing?.iscompleted;

            let step;

            if (!isShippingCompleted || urlParams.step === 'shipping' || (!isBillingCompleted && !urlParams.step)) {
                step = 'shipping';
            } else if (!isBillingCompleted) {
                step = 'billing';
            } else if (typeof urlParams.step === 'string') {
                step = urlParams.step;
            } else if (urlParams.stage === 'payment' || urlParams.stage === 'placeOrder') {
                // placeOrder mainly used in case of error from payment integration
                // in SFRA payment error was shown on review step, in Launch 360 on Billing step
                step = 'billing';
            } else {
                step = 'summary';
            }

            return step;
        }

        /**
         * @description Initialize widget events
         */
        initEvents() {
            this.ev('popstate', () => {
                const urlParams = getUrlParams();

                if (typeof urlParams.step === 'string') {
                    this.isStepActivatedFromHistory = true;
                    this.activateStepByName(urlParams.step);
                }
            }, window);
        }

        /**
         * @description Activates checkout step by step name
         * @param name - Name of step
         * @param historyReplace - If we need to replace history state instead of push it
         */
        activateStepByName(name: string, historyReplace = false) {
            let isStepActivated = false;

            if (name === 'customer' && this.order?.orderEmail && this.isStepActivatedFromHistory) {
                window.location.href = this.prefs().urlCart;

                return;
            }

            this.prefs().stepsList.split(',').forEach(step => {
                if (step === name) {
                    isStepActivated = true;
                    this.prevStep = '' + this.currentStep;
                    this.currentStep = step;

                    this.activateStep(); // Call CheckoutStepMixin.ts methods
                    this[step + 'Activate'].call(this); // Call step + StepMixin.ts methods

                    const urlParams = getUrlParams();

                    if (urlParams.step !== step) {
                        window.history[historyReplace ? 'replaceState' : 'pushState'](
                            undefined,
                            '',
                            appendParamsToUrl(this.prefs().urlCheckout, { step })
                        );
                    }
                } else {
                    this.deactivateStep(step, !isStepActivated);
                    this[step + 'DeActivate'].call(this, !isStepActivated);
                }
            });
            this.isStepActivatedFromHistory = false;
        }

        /**
         * @description Get data for next step button
         * @returns Next step button data
         */
        getNextButtonData() {
            const shipping = (this.order || this.prefs().basket).shipping;
            const isMethodsAvailable = Boolean(get(shipping, '0.applicableShippingMethods.length'));
            const isError = Boolean(get(shipping, '0.applicableShippingMethodsError'));
            const hasShippingMethods = isMethodsAvailable && !isError;

            return {
                [String(this.currentStep)]: true,
                disabled: hasShippingMethods ? '' : 'disabled',
                hasShippingMethods,
                feedbackMsg: '',
                skipSummaryStep: this.prefs().skipSummaryStep
            };
        }

        /**
         * @description Submit current step
         * @param data - additional data for steps
         * @listens dom#click
         */
        submitCurrentStep(...data) {
            this[this.currentStep + 'Submit'].call(this, data);
        }

        /**
         * @description Gets the name of next checkout step
         */
        getNextStepName() {
            const nextSteps = this.prefs().stepsList.split(',');

            // eslint-disable-next-line no-empty
            while (nextSteps.length && this.currentStep !== nextSteps.shift()) {}

            const nextStepName = nextSteps.length ? nextSteps : this.prefs().stepsList.split(',');

            return '' + nextStepName.shift();
        }

        /**
         * @description Activate next step
         */
        nextStep() {
            const nextStep = this.getNextStepName();

            this.activateStepByName(nextStep);

            // Handle focus logic CheckoutStepMixin - setFocusOnEditButton
            this.callIfExists('setFocusOnEditButton', this.prevStep);
        }

        /**
         * @description Handle click on step edit button
         * @param el - Edit button ref element
         * @listens dom#click
         */
        editStep(el: TRefElementInstance) {
            switch (el.data('step')) {
                case 'shipping':
                    this.activateStepByName('shipping');
                    // Handle focus logic CheckoutStepMixin - setFocusOnFirstInput
                    this.callIfExists('setFocusOnFirstInput', 'shipping');
                    break;
                case 'billing':
                    this.activateStepByName('billing');
                    // Handle focus logic CheckoutStepMixin - setFocusOnFirstInput
                    this.callIfExists('setFocusOnFirstInput', 'billing');
                    break;
                default:
                    break;
            }
        }

        /**
         * Updates error label according to next button data
         *
         * @param errorContext Widget that contains error label
         */
        updateErrorMessage(errorContext: InstanceType<TWidget>) {
            errorContext.getById(this.currentFormId, (currentForm: TShippingFormInstance | TBillingFormInstance) => {
                const nextButtonData = this.getNextButtonData();
                const errorMessage = nextButtonData.feedbackMsg;
                const errorFeedback = currentForm.ref('errorFeedback');

                if (errorMessage.length) {
                    errorFeedback.setText(errorMessage).show().attr('role', 'alert');
                    scrollWindowTo(errorFeedback.get(), true);
                } else {
                    errorFeedback.setText('').hide().attr('role', false);
                }

                this.eventBus().emit('checkout.step.updated', nextButtonData);
            });
        }

        /**
         * Shows/hides error label after receiving response from customer/shipping/billing submit request
         *
         * @param context Widget that contains error label
         */
        updateNextButton(context: InstanceType<TWidget> = this) {
            this.updateErrorMessage(context);
        }

        /**
         * @description Overlapped method
         */
        shippingSubmit() {}

        /**
         * @description Overlapped method
         * @param submitButtons Event target
         */
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        billingSubmit(...submitButtons) {}

        /**
         * @description Overlapped method
         */
        billingActivate() {}

        /**
         * @description Overlapped method
         * @param additionalData - additional data with "Place Order" button for disabling/enabling
         * @param skipPrivacyValidation - Allows skip privacy validation if needed(e.g. PayPal)
         */
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        summarySubmit(additionalData, skipPrivacyValidation) {}

        /**
         * @description Summary submission result handler
         * @param response - Server response object
         */
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        summarySubmitted(response) {
            // Placeholder for additional logic on summary submission
        }

        /**
         * @description Overlapped method
         * @param response - Server response object
         * @param submitBtn - submit button for enabling
         */
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        handleSummarySubmissionResponse(response, submitBtn) {}

        /**
         * @description On customer login handler
         */
        onCustomerLogin() {
            this.eventBus().emit('checkout.customer.login', this);
        }
    }

    return CheckoutMgr;
}

export type TCheckoutMgr = ReturnType<typeof CheckoutMgrClassCreator>;

export type TCheckoutMgrInstance = InstanceType<TCheckoutMgr>;

export default CheckoutMgrClassCreator;
